import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Button, Select, Spin } from 'antd';
import type { EntityLookupItem, EntityLookupType } from '../../store/api/entityLookupApi';
import { useLazyLookupEntitiesQuery } from '../../store/api/entityLookupApi';
import './SearchSelect.css';

export type SearchSelectProps = {
  entityType: EntityLookupType;
  value?: number;
  onChange?: (value: number | undefined, item?: EntityLookupItem) => void;
  placeholder?: string;
  allowClear?: boolean;
  disabled?: boolean;
  pageSize?: number;
};

function formatEntityLabel(item: EntityLookupItem): string {
  return [item.primaryLine, item.secondaryLine, item.tertiaryLine].filter(Boolean).join(' · ');
}

function renderEntityOption(item: EntityLookupItem) {
  return (
    <div>
      <div>{item.primaryLine}</div>
      {item.secondaryLine ? (
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>{item.secondaryLine}</div>
      ) : null}
      {item.tertiaryLine ? (
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{item.tertiaryLine}</div>
      ) : null}
    </div>
  );
}

function mergeUniqueItems(existing: EntityLookupItem[], incoming: EntityLookupItem[]): EntityLookupItem[] {
  const byId = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) byId.set(item.id, item);
  return Array.from(byId.values());
}

export function formatSearchSelectCounter(shownCount: number, totalCount: number, isSearch: boolean): string {
  if (isSearch) {
    return `Показано ${shownCount} из ${totalCount} найденных`;
  }
  return `Показано ${shownCount} из ${totalCount}`;
}

export function formatSearchSelectLoadMoreLabel(remainingCount: number, pageSize: number): string {
  const nextBatch = Math.min(pageSize, remainingCount);
  return `Показать еще ${nextBatch}`;
}

type SearchSelectFooterProps = {
  shownCount: number;
  totalCount: number;
  hasTypedSearch: boolean;
  hasMore: boolean;
  remainingCount: number;
  pageSize: number;
  isLoadingMore: boolean;
  onLoadMore: () => void;
};

function SearchSelectFooter({
  shownCount,
  totalCount,
  hasTypedSearch,
  hasMore,
  remainingCount,
  pageSize,
  isLoadingMore,
  onLoadMore,
}: SearchSelectFooterProps) {
  const showLoadMore = hasMore && remainingCount > 0;

  return (
    <div className="search-select-footer">
      <p
        className={`search-select-footer__counter${showLoadMore ? '' : ' search-select-footer__counter--compact'}`}
      >
        {formatSearchSelectCounter(shownCount, totalCount, hasTypedSearch)}
      </p>
      {showLoadMore ? (
        <>
          <div className="search-select-footer__divider" aria-hidden="true" />
          <Button
            type="text"
            block
            className="search-select-footer__load-more"
            icon={<DownOutlined />}
            loading={isLoadingMore}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onLoadMore();
            }}
          >
            {formatSearchSelectLoadMoreLabel(remainingCount, pageSize)}
          </Button>
        </>
      ) : null}
    </div>
  );
}

const SearchSelect: React.FC<SearchSelectProps> = ({
  entityType,
  value,
  onChange,
  placeholder,
  allowClear = true,
  disabled = false,
  pageSize = 20,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedItem, setSelectedItem] = useState<EntityLookupItem | null>(null);
  const [options, setOptions] = useState<EntityLookupItem[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasTypedSearch, setHasTypedSearch] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const activeQueryRef = useRef('');
  const [lookupEntities, { isFetching }] = useLazyLookupEntitiesQuery();

  const fetchPage = useCallback(
    async (query: string, page: number, append: boolean) => {
      const trimmed = query.trim();
      activeQueryRef.current = trimmed;
      const result = await lookupEntities({
        type: entityType,
        ...(trimmed ? { q: trimmed } : {}),
        limit: pageSize,
        offset: page * pageSize,
      }).unwrap();

      if (activeQueryRef.current !== trimmed) return null;

      setHasTypedSearch(trimmed.length > 0);
      setCurrentPage(page);
      setHasMore(result.hasMore);
      setTotalCount(result.totalCount);
      setOptions((prev) => (append ? mergeUniqueItems(prev, result.items) : result.items));
      return result;
    },
    [entityType, lookupEntities, pageSize]
  );

  const loadOptions = useCallback(
    async (query?: string) => {
      await fetchPage(String(query ?? ''), 0, false);
    },
    [fetchPage]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || isFetching) return;
    setIsLoadingMore(true);
    try {
      await fetchPage(searchText, currentPage + 1, true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, fetchPage, hasMore, isFetching, isLoadingMore, searchText]);

  const hydrateSelected = useCallback(
    async (id: number) => {
      const result = await lookupEntities({ type: entityType, id }).unwrap();
      const item = result.items[0];
      if (item) setSelectedItem(item);
    },
    [entityType, lookupEntities]
  );

  useEffect(() => {
    if (value == null) {
      setSelectedItem(null);
      return;
    }
    if (selectedItem?.id === value) return;
    void hydrateSelected(value);
  }, [value, hydrateSelected, selectedItem?.id]);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    },
    []
  );

  const scheduleSearch = useCallback(
    (query: string) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      const trimmed = query.trim();
      if (!trimmed) {
        void loadOptions();
        return;
      }
      setOptions([]);
      setHasMore(false);
      setTotalCount(0);
      setCurrentPage(0);
      searchTimeoutRef.current = setTimeout(() => {
        void loadOptions(trimmed);
      }, 250);
    },
    [loadOptions]
  );

  const handleOpenChange = (open: boolean) => {
    setDropdownOpen(open);
    if (open && !searchText.trim()) {
      void loadOptions();
    }
    if (!open) {
      setSearchText('');
      setHasTypedSearch(false);
    }
  };

  const displayOptions = useMemo(() => {
    const byId = new Map<number, EntityLookupItem>();
    for (const item of options) byId.set(item.id, item);
    if (selectedItem) byId.set(selectedItem.id, selectedItem);
    return Array.from(byId.values());
  }, [options, selectedItem]);

  const selectOptions = useMemo(
    () =>
      displayOptions.map((item) => ({
        value: item.id,
        label: formatEntityLabel(item),
        item,
      })),
    [displayOptions]
  );

  const isInitialLoading = isFetching && !isLoadingMore && options.length === 0;
  const showNotFound = hasTypedSearch && !isInitialLoading && !isLoadingMore && displayOptions.length === 0;

  const shownCount = options.length;
  const remainingCount = Math.max(totalCount - shownCount, 0);
  const showFooter = totalCount > 0 && shownCount > 0 && !isInitialLoading;

  const dropdownRender = (menu: React.ReactElement) => (
    <>
      {menu}
      {showFooter ? (
        <SearchSelectFooter
          shownCount={shownCount}
          totalCount={totalCount}
          hasTypedSearch={hasTypedSearch}
          hasMore={hasMore}
          remainingCount={remainingCount}
          pageSize={pageSize}
          isLoadingMore={isLoadingMore}
          onLoadMore={() => {
            void loadMore();
          }}
        />
      ) : null}
    </>
  );

  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      allowClear={allowClear}
      showSearch
      filterOption={false}
      open={dropdownOpen}
      onOpenChange={handleOpenChange}
      searchValue={searchText}
      value={value ?? undefined}
      placeholder={placeholder}
      options={selectOptions}
      dropdownRender={dropdownRender}
      optionRender={(option) => {
        const item = (option.data as { item?: EntityLookupItem }).item;
        return item ? renderEntityOption(item) : option.label;
      }}
      notFoundContent={
        isInitialLoading ? <Spin size="small" /> : showNotFound ? 'Ничего не найдено' : null
      }
      onSearch={(text) => {
        setSearchText(text);
        if (selectedItem && value != null) {
          setSelectedItem(null);
          onChange?.(undefined);
        }
        scheduleSearch(text);
      }}
      onChange={(nextValue) => {
        if (nextValue == null) {
          setSelectedItem(null);
          setSearchText('');
          setHasTypedSearch(false);
          onChange?.(undefined);
          void loadOptions();
          setDropdownOpen(true);
          return;
        }
        const id = Number(nextValue);
        const item = displayOptions.find((row) => row.id === id);
        if (!item) return;
        setSelectedItem(item);
        setSearchText('');
        setHasTypedSearch(false);
        setDropdownOpen(false);
        onChange?.(id, item);
      }}
    />
  );
};

export default SearchSelect;
